import React from 'react';
import PropTypes from 'prop-types';
import {useHistory} from 'react-router-dom';
import {useDispatch} from 'react-redux';
import {DISCOUNT_MODALS_V2} from 'helpers/constants/discountsV2';
import {REPORT_DATEPICKER_INDEX, REPORT_TYPES} from 'helpers/constants/reports';
import {PUSH_MODALS_STACK} from 'store/discountsV2/action';
import IconCoupon from 'assets/mainPage/growthPrograms/icon-discounts-coupon.svg';
import IconOrdersRedeemed from 'assets/mainPage/growthPrograms/icon-orders-redeemed.svg';
import IconCashbackCycle from 'assets/mainPage/growthPrograms/icon-cashback-cycle.svg';
import IconCashbackEarned from 'assets/mainPage/growthPrograms/icon-cashback-earned.svg';
import IconCashbackRedeemed from 'assets/mainPage/growthPrograms/icon-cashback-redeemed.svg';
import IconClockGrey from 'assets/mainPage/growthPrograms/icon-clock-grey.svg';
import {
  GrowthPanel,
  GrowthPanelHeader,
  GrowthPanelLink,
  GrowthPanelMetric,
  GrowthPanelPill,
  GrowthPanelSection,
  GrowthPanelSections
} from 'routes/App/MainPage/Components/GrowthPrograms/styles';
import {
  formatCount,
  formatCurrency,
  formatTrendPercent,
  getCashbackWidgetState
} from 'routes/App/MainPage/Components/GrowthPrograms/utils';

const CASHBACK_EARLY_MESSAGE =
  'Credits are building — redemptions will appear here soon';
const DISCOUNTS_EMPTY_MESSAGE =
  'Attract new customers and boost order volume with targeted offers';

const GrowthProgramsPanel = ({
  activeCount,
  isEnrolled,
  redeemedOrdersTotal,
  discountsOrdersRedeemed,
  discountsOrdersRedeemedTrend,
  ordersEarned,
  ordersEarnedTrend,
  ordersRedeemed,
  ordersRedeemedTrend
}) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const hasActiveDiscounts = Number(activeCount) > 0;
  const cashbackState = getCashbackWidgetState({
    isEnrolled,
    redeemedOrdersTotal
  });
  const earnedTrend = formatTrendPercent(ordersEarnedTrend);
  const redeemedTrend = formatTrendPercent(ordersRedeemedTrend);
  const discountsRedeemedTrend = formatTrendPercent(
    discountsOrdersRedeemedTrend
  );
  const cashbackPill =
    cashbackState === 'early'
      ? {icon: IconClockGrey, text: CASHBACK_EARLY_MESSAGE}
      : null;
  const discountsPill = !hasActiveDiscounts
    ? {icon: null, text: DISCOUNTS_EMPTY_MESSAGE}
    : null;

  const renderPill = (pill) =>
    pill && (
      <GrowthPanelPill>
        {pill.icon && <img className='pillIcon' src={pill.icon} alt='' />}
        {pill.text}
      </GrowthPanelPill>
    );

  const goToManageDiscounts = () => {
    history.push('/discounts-v2/your-discounts');
  };

  const goToCreateDiscount = () => {
    dispatch(PUSH_MODALS_STACK({type: DISCOUNT_MODALS_V2.SELECT}));
    history.push('/discounts-v2/your-discounts');
  };

  const requestUsageReport = () => {
    history.push(
      `/reports?type=${REPORT_TYPES.CASHBACK_USAGE}&period=${REPORT_DATEPICKER_INDEX.ALL_PERIOD}`
    );
  };

  const goToCashback = () => {
    history.push('/cashback');
  };

  const renderDiscountsSection = () => (
    <GrowthPanelSection>
      <GrowthPanelHeader>
        <img className='icon' src={IconCoupon} alt='' />
        <span className='title'>
          {hasActiveDiscounts ? 'Active Discounts' : 'Discounts'}
        </span>
      </GrowthPanelHeader>
      <GrowthPanelMetric>{formatCount(activeCount)}</GrowthPanelMetric>
      {renderPill(discountsPill)}
      <GrowthPanelLink
        onClick={hasActiveDiscounts ? goToManageDiscounts : goToCreateDiscount}
      >
        {hasActiveDiscounts ? 'Manage discounts' : 'Add your first discount'}
      </GrowthPanelLink>
    </GrowthPanelSection>
  );

  const renderDiscountsRedeemedSection = () =>
    hasActiveDiscounts && (
      <GrowthPanelSection>
        <GrowthPanelHeader>
          <img className='icon' src={IconOrdersRedeemed} alt='' />
          <span className='title'>Orders Redeemed Discounts</span>
        </GrowthPanelHeader>
        <GrowthPanelMetric>
          {formatCount(discountsOrdersRedeemed)}
          {discountsRedeemedTrend && (
            <span className={`delta${discountsRedeemedTrend.className}`}>
              {discountsRedeemedTrend.label}
            </span>
          )}
        </GrowthPanelMetric>
        <GrowthPanelLink onClick={requestUsageReport}>
          Download usage report
        </GrowthPanelLink>
      </GrowthPanelSection>
    );

  const renderCashbackSingleSection = () => (
    <GrowthPanelSection>
      <GrowthPanelHeader>
        <img className='icon' src={IconCashbackCycle} alt='' />
        <span className='title'>Cashback</span>
      </GrowthPanelHeader>
      <GrowthPanelMetric>
        {formatCurrency(ordersEarned)}
        {earnedTrend && (
          <span className={`delta${earnedTrend.className}`}>
            {earnedTrend.label}
          </span>
        )}
      </GrowthPanelMetric>
      {renderPill(cashbackPill)}
      <GrowthPanelLink onClick={goToCashback}>
        View tips to boost
      </GrowthPanelLink>
    </GrowthPanelSection>
  );

  const renderCashbackSplitSections = () => (
    <>
      <GrowthPanelSection>
        <GrowthPanelHeader>
          <img className='icon' src={IconCashbackEarned} alt='' />
          <span className='title'>Cashback Earned</span>
        </GrowthPanelHeader>
        <GrowthPanelMetric>
          {formatCurrency(ordersEarned)}
          {earnedTrend && (
            <span className={`delta${earnedTrend.className}`}>
              {earnedTrend.label}
            </span>
          )}
        </GrowthPanelMetric>
        <GrowthPanelLink onClick={goToCashback}>View details</GrowthPanelLink>
      </GrowthPanelSection>
      <GrowthPanelSection>
        <GrowthPanelHeader>
          <img className='icon' src={IconCashbackRedeemed} alt='' />
          <span className='title'>Cashback Redeemed</span>
        </GrowthPanelHeader>
        <GrowthPanelMetric>
          {formatCurrency(ordersRedeemed)}
          {redeemedTrend && (
            <span className={`delta${redeemedTrend.className}`}>
              {redeemedTrend.label}
            </span>
          )}
        </GrowthPanelMetric>
      </GrowthPanelSection>
    </>
  );

  return (
    <GrowthPanel>
      <GrowthPanelSections>
        {renderDiscountsSection()}
        {renderDiscountsRedeemedSection()}
        {cashbackState === 'healthy'
          ? renderCashbackSplitSections()
          : renderCashbackSingleSection()}
      </GrowthPanelSections>
    </GrowthPanel>
  );
};

GrowthProgramsPanel.propTypes = {
  activeCount: PropTypes.number,
  isEnrolled: PropTypes.bool,
  redeemedOrdersTotal: PropTypes.number,
  discountsOrdersRedeemed: PropTypes.number,
  discountsOrdersRedeemedTrend: PropTypes.number,
  ordersEarned: PropTypes.number,
  ordersEarnedTrend: PropTypes.number,
  ordersRedeemed: PropTypes.number,
  ordersRedeemedTrend: PropTypes.number
};

GrowthProgramsPanel.defaultProps = {
  activeCount: 0,
  isEnrolled: false,
  redeemedOrdersTotal: 0,
  discountsOrdersRedeemed: 0,
  discountsOrdersRedeemedTrend: null,
  ordersEarned: 0,
  ordersEarnedTrend: null,
  ordersRedeemed: 0,
  ordersRedeemedTrend: null
};

export default GrowthProgramsPanel;
